import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { merkeSessionEnde, pb } from '../lib/pocketbase.js';

const AuthContext = createContext(null);

const GESPERRT_MELDUNG =
  'Dein Zugang wurde gesperrt. Bitte wende dich an einen Administrator.';

const SESSION_UNGUELTIG_MELDUNG =
  'Deine Sitzung ist nicht mehr gültig. Bitte melde dich erneut an.';

// Wie oft eine offene App ihre Session gegenprüft, und wie oft das
// höchstens passiert, wenn Fokus- und Sichtbarkeits-Events zusammenfallen.
const REVALIDIERUNG_INTERVALL_MS = 5 * 60 * 1000;
const REVALIDIERUNG_MIN_ABSTAND_MS = 60 * 1000;

/**
 * Erkennt die Sperr-Absage aus pb/pb_hooks/user_gesperrt.pb.js.
 *
 * Erkennungsmerkmal ist der Status 403: PocketBase beantwortet einen
 * fehlgeschlagenen Login sonst immer mit 400 ("Failed to authenticate."),
 * ein 403 auf dieser Route kommt ausschließlich aus unserem Hook. Ein
 * mitgegebenes data-Objekt wäre kein verlässlicheres Signal — PB deutet das
 * data-Feld als Feld-Validierungsfehler um und schreibt den Inhalt platt.
 */
function istGesperrtFehler(err) {
  if (err?.status === 403) return true;
  const message = err?.response?.message;
  return typeof message === 'string' && message.toLowerCase().includes('gesperrt');
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(() => pb.authStore.record ?? pb.authStore.model ?? null);

  useEffect(() => {
    // PB ruft den Listener bei Login/Logout/Refresh.
    const unsubscribe = pb.authStore.onChange(() => {
      setUser(pb.authStore.record ?? pb.authStore.model ?? null);
    }, true);

    let entladen = false;
    let letztePruefung = 0;

    /**
     * Frischt Token + User-Record auf und beendet die Session, wenn das
     * fehlschlägt.
     *
     * Warum das aktiv passieren muss statt nur reaktiv auf Fehler:
     * PocketBase ignoriert ein ungültiges Token bei normalen Requests
     * stillschweigend und behandelt sie als Gast. Ein gesperrter User mit
     * offenem Tab bekäme sonst überall leere Listen statt eines Rauswurfs —
     * auth-refresh ist die Route, die dabei ehrlich mit 401/403 antwortet.
     *
     * Nebeneffekt, gewollt: Eine Rollenänderung wird ohne Re-Login wirksam.
     * Dass der Refresh die Token-Laufzeit verlängert, ist unkritisch — die
     * Sitzungsdauer begrenzt ohnehin useInactivityLogout (30 Min).
     */
    async function pruefeSession() {
      if (entladen || !pb.authStore.isValid) return;

      const jetzt = Date.now();
      if (jetzt - letztePruefung < REVALIDIERUNG_MIN_ABSTAND_MS) return;
      letztePruefung = jetzt;

      try {
        await pb.collection('users').authRefresh();
      } catch (err) {
        // status 0 = Netzwerk-/Offline-Fehler. Den User dafür auszuloggen
        // wäre falsch, sein Token kann völlig in Ordnung sein.
        if (entladen || err?.status === 0) return;
        merkeSessionEnde(istGesperrtFehler(err) ? GESPERRT_MELDUNG : SESSION_UNGUELTIG_MELDUNG);
        pb.authStore.clear();
      }
    }

    // Beim App-Start einmal, danach periodisch und immer dann, wenn der Tab
    // wieder in den Vordergrund kommt — genau der Moment, in dem eine
    // zwischenzeitlich gesperrte Session auffallen soll.
    pruefeSession();

    const intervall = setInterval(pruefeSession, REVALIDIERUNG_INTERVALL_MS);
    const beiRueckkehr = () => {
      if (document.visibilityState === 'visible') pruefeSession();
    };
    document.addEventListener('visibilitychange', beiRueckkehr);
    window.addEventListener('focus', beiRueckkehr);

    return () => {
      entladen = true;
      clearInterval(intervall);
      document.removeEventListener('visibilitychange', beiRueckkehr);
      window.removeEventListener('focus', beiRueckkehr);
      unsubscribe();
    };
  }, []);

  const login = useCallback(async (email, password) => {
    try {
      await pb.collection('users').authWithPassword(email, password);
    } catch (err) {
      if (istGesperrtFehler(err)) {
        // Der Hook bricht ab, bevor ein Token ausgestellt wird — clear() ist
        // hier nur Absicherung gegen halb geschriebene Auth-States.
        pb.authStore.clear();
        throw new Error(GESPERRT_MELDUNG);
      }
      throw err;
    }
  }, []);

  const logout = useCallback(() => {
    pb.authStore.clear();
  }, []);

  const value = useMemo(() => {
    const rolle = user?.rolle;
    const isAdmin = rolle === 'admin';
    const isFuehrungskraft = rolle === 'fuehrungskraft';
    return {
      user,
      isAuthenticated: pb.authStore.isValid && !!user,
      isAdmin,
      isFuehrungskraft,
      // Sammelflag für "darf Partner verwalten / Auswertung sehen": Admin
      // ist immer auch Führungskraft im Sinne der Berechtigungen.
      isFuehrungskraftOrAdmin: isAdmin || isFuehrungskraft,
      login,
      logout,
    };
  }, [user, login, logout]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error('useAuth muss innerhalb von <AuthProvider> verwendet werden');
  }
  return ctx;
}
